import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DollarSign, Receipt, CheckCircle2, Clock } from "lucide-react";

const STATUS_COLOR = {
  pending: "text-amber-800 bg-amber-50 border-amber-200",
  paid: "text-emerald-700 bg-emerald-50 border-emerald-200",
  canceled: "text-gray-700 bg-gray-100 border-gray-200",
};

function fmtUSD(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));
}

/**
 * Invoices card for per-claim success-fee billing (Stripe Checkout).
 * - Admins can create an invoice (settlement_amount + fee_pct).
 * - Policyholders see outstanding invoices with a "Pay fee" button that
 *   opens Stripe Checkout in a new tab and polls status on return.
 */
export default function InvoicesCard({ claim, userRole }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [settlement, setSettlement] = useState("");
  const [feePct, setFeePct] = useState("15");
  const [notes, setNotes] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/invoices?claim_id=${claim.id}`);
      setInvoices(r.data || []);
    } catch {
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [claim.id]);

  useEffect(() => {
    load();
  }, [load]);

  // If we were redirected back from Stripe, poll status to converge the invoice
  const sessionId = searchParams.get("session_id");
  const invoiceIdParam = searchParams.get("invoice_id");
  const canceled = searchParams.get("canceled");
  useEffect(() => {
    if (canceled) {
      toast.info("Checkout canceled");
      searchParams.delete("canceled");
      setSearchParams(searchParams, { replace: true });
      return;
    }
    if (!sessionId) return;
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/invoices/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          toast.success("Payment received — thank you!");
          await load();
          searchParams.delete("session_id");
          searchParams.delete("invoice_id");
          setSearchParams(searchParams, { replace: true });
          return;
        }
        if (!cancelled && attempts < 6) {
          setTimeout(poll, 2000);
        } else if (!cancelled) {
          toast.info("Payment is still processing — refresh in a moment.");
          searchParams.delete("session_id");
          searchParams.delete("invoice_id");
          setSearchParams(searchParams, { replace: true });
        }
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Status check failed");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, canceled]);

  const onCreate = async () => {
    const amt = parseFloat(settlement);
    const pct = parseFloat(feePct);
    if (!amt || amt <= 0) return toast.error("Enter a settlement amount");
    if (!pct || pct <= 0 || pct > 50) return toast.error("fee_pct must be 0–50");
    setCreating(true);
    try {
      await api.post("/invoices", {
        claim_id: claim.id,
        settlement_amount: amt,
        fee_pct: pct,
        notes: notes || null,
      });
      toast.success("Invoice created");
      setSettlement("");
      setNotes("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const onPay = async (inv) => {
    try {
      const r = await api.post(`/invoices/${inv.id}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    }
  };

  if (userRole === "contractor") return null;

  return (
    <Card className="border-[#E5E7EB]" data-testid="invoices-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-['Outfit'] flex items-center gap-2">
          <Receipt className="w-5 h-5 text-[#C25934]" />
          Success-fee invoices
        </CardTitle>
        {invoiceIdParam && (
          <span className="text-xs text-gray-500">
            Verifying payment for #{String(invoiceIdParam).slice(0, 8)}…
          </span>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {userRole === "admin" && (
          <div className="rounded-md border border-[#E5E7EB] bg-[#F9F8F6] p-4 space-y-3">
            <p className="text-sm text-[#1E3F33] font-medium">
              Create invoice for this claim
            </p>
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-gray-600">Settlement amount (USD)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={settlement}
                  onChange={(e) => setSettlement(e.target.value)}
                  placeholder="10000"
                  data-testid="invoice-settlement-amount"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Fee %</Label>
                <Input
                  type="number"
                  min="0.1"
                  max="50"
                  step="0.1"
                  value={feePct}
                  onChange={(e) => setFeePct(e.target.value)}
                  data-testid="invoice-fee-pct"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Notes (optional)</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Post-settlement fee"
                  data-testid="invoice-notes"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {settlement && feePct
                  ? `Will charge ${fmtUSD(parseFloat(settlement || 0) * parseFloat(feePct || 0) / 100)}`
                  : "Enter settlement + % to preview charge"}
              </span>
              <Button
                onClick={onCreate}
                disabled={creating || !settlement || !feePct}
                className="bg-[#1E3F33] hover:bg-[#162f26] text-white"
                data-testid="invoice-create-btn"
              >
                <DollarSign className="w-4 h-4" />
                {creating ? "Creating…" : "Create invoice"}
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-gray-500">Loading invoices…</p>
        ) : invoices.length === 0 ? (
          <p className="text-sm text-gray-500">
            No invoices yet for this claim.
          </p>
        ) : (
          <div className="space-y-2" data-testid="invoices-list">
            {invoices.map((inv) => (
              <div
                key={inv.id}
                className="flex flex-wrap items-center gap-3 border border-[#E5E7EB] rounded-md px-4 py-3"
                data-testid={`invoice-row-${inv.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1E3F33]">
                    {fmtUSD(inv.amount)}{" "}
                    <span className="text-xs text-gray-500 font-normal">
                      · {inv.fee_pct}% of {fmtUSD(inv.settlement_amount)}
                    </span>
                  </p>
                  <p className="text-xs text-gray-500">
                    {inv.created_at?.slice(0, 10)} · #{inv.id.slice(0, 8)}
                    {inv.notes ? ` · ${inv.notes}` : ""}
                  </p>
                </div>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLOR[inv.status] || STATUS_COLOR.pending}`}
                >
                  {inv.status === "paid" ? (
                    <span className="inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Paid
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {inv.status}
                    </span>
                  )}
                </span>
                {userRole === "policyholder" && inv.status === "pending" && (
                  <Button
                    onClick={() => onPay(inv)}
                    className="bg-[#C25934] hover:bg-[#a94a2b] text-white"
                    size="sm"
                    data-testid={`invoice-pay-${inv.id}`}
                  >
                    <DollarSign className="w-3.5 h-3.5" />
                    Pay fee
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
